import { Trans, t } from "@lingui/macro";
import {
  Box,
  Button,
  Container,
  Paper,
  Skeleton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { MenuList, WfIconNavigationCloseRegular } from "@WahooFitness/redesignr";
import {
  postRoute,
  PullToRefresh,
  useDialogContext,
  GlobalHeader,
  WarmingUp,
} from "@WahooFitness/wahoo-offline-mfe";
import WSMSensorConnectionState from "@WahooFitness/wsm-native/dist/esm/types/connection_state";
import { FocusEvent, useCallback, useEffect, useState } from "react";
import useDrawerPresentation from "@/hooks/useDrawerPresentation";
import BatteryLevelIcon from "./BatteryLevelIcon";
import DisconnectedAlert from "./DisconnectedAlert";
import SignalStrengthIcon from "./SignalStrengthIcon";
import { useDeviceConfig } from "./useDeviceConfig";
import BoltProductImage from "./BoltProductImage";

function DeviceConfig({ appToken }: { appToken: string }) {
  const { palette } = useTheme();
  const {
    deviceConfigLoading,
    device,
    unpairDevice,
    setDeviceName,
    connectionStateDisplay,
    profilesSettingsMenu,
    remoteSensorMenu,
    deviceInfoSetupMenu,
    refreshDeviceConfig,
  } = useDeviceConfig(appToken);

  const { setDialog, handleClose } = useDialogContext();
  const { isDrawer, close } = useDrawerPresentation("deviceConfig");

  const handleUnpairDevice = useCallback(async () => {
    setDialog({
      open: true,
      title: t`Unpair ELEMNT`,
      body: t`Are you sure you want to unpair your ELEMNT?`,
      actions: [
        { text: t`Cancel`, action: handleClose },
        {
          text: t`Unpair`,
          color: "error",
          action: async () => {
            await unpairDevice();
            postRoute("home");
          },
        },
      ],
    });
  }, [handleClose, setDialog, unpairDevice]);

  const [displayNameInitialized, setDisplayNameInitialized] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [displayNameInputValue, setDisplayNameInputValue] = useState("");

  useEffect(() => {
    if (!displayNameInitialized && device?.displayName) {
      setDisplayName(device?.displayName || device?.name || "");
      setDisplayNameInputValue(device?.displayName || device?.name || "");
      setDisplayNameInitialized(true);
    }
  }, [device?.displayName, device?.name, displayNameInitialized]);

  const onDisplayNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.value.includes("\n")) {
        return;
      }
      setDisplayName(event.target.value || device?.name || "");
      setDisplayNameInputValue(event.target.value);
      setDeviceName(event.target.value);
    },
    [device?.name, setDeviceName]
  );

  const onDisplayNameFocus = useCallback((event: FocusEvent<HTMLInputElement>) => {
    event.target.select();
  }, []);

  //TODO: We want a different animation for this (pop instead of push)
  const navigateHome = useCallback(() => {
    postRoute("home");
  }, []);

  if (deviceConfigLoading) {
    return <WarmingUp />;
  }

  if (!device) {
    return <Box>Device not paired</Box>;
  }

  return (
    <>
      <PullToRefresh onRefresh={refreshDeviceConfig} />
      <GlobalHeader
        primaryLeftActionItem={
          isDrawer ? { icon: <WfIconNavigationCloseRegular />, action: close } : undefined
        }
        backAction={isDrawer ? close : navigateHome}
      />
      <Container maxWidth="sm" disableGutters sx={{ minHeight: "100%" }}>
        <Box
          display="flex"
          flexDirection="column"
          overflow="hidden"
          width="100%"
          height="100%"
          px={2}
        >
          <DisconnectedAlert device={device} />
          <Box maxWidth="sm" display="flex" flexDirection="column" overflow="scroll" py={2} gap={4}>
            <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
              <BoltProductImage
                boltType={device.boltType}
                appToken={appToken}
                height={150}
                fallback={<Skeleton variant="rectangular" height={150} width={94} />}
              />
              <Typography variant="ui-2xl-medium">{displayName}</Typography>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="center"
                alignItems="flex-end"
                gap={2}
              >
                <Box display="flex" flexDirection="row" gap={0.5}>
                  {device?.connectionState === WSMSensorConnectionState.CONNECTED && (
                    <SignalStrengthIcon rssi={device?.rssi} />
                  )}
                  <Typography variant="ui-lg-medium" color="text.secondary">
                    {connectionStateDisplay}
                  </Typography>
                </Box>
                {device?.connectionState === WSMSensorConnectionState.CONNECTED &&
                  device?.battery?.percent !== undefined &&
                  (device.battery.percent !== -1 || device.battery.charging) && (
                    <Box display="flex" flexDirection="row" gap={0.5}>
                      <BatteryLevelIcon
                        batteryPercentage={device.battery.percent}
                        isCharging={!!device.battery.charging}
                      />
                      <Typography variant="ui-lg-medium" color="text.secondary">
                        {t`${device.battery.percent}%`}
                      </Typography>
                    </Box>
                  )}
              </Box>
            </Box>
            <Box width="100%">
              <Paper elevation={4}>
                <Box p={2}>
                  <TextField
                    variant="filled"
                    label={t`Name`}
                    value={displayNameInputValue}
                    onChange={onDisplayNameChange}
                    onFocus={onDisplayNameFocus}
                    fullWidth
                    multiline
                  />
                </Box>
              </Paper>
            </Box>
            <MenuList listItems={profilesSettingsMenu} disableGutters />
            {!!remoteSensorMenu?.length && (
              <Box display="flex" flexDirection="column">
                <Typography variant="ui-base-medium" color={palette.text.secondary} mx={2}>
                  <Trans>Paired sensors</Trans>
                </Typography>
                <MenuList listItems={remoteSensorMenu} disableGutters />
                <Typography variant="ui-base-medium" color={palette.text.secondary} mx={2}>
                  <Trans>
                    Only sensors paired directly from your ELEMNT will show up on this list.
                  </Trans>
                </Typography>
              </Box>
            )}
            <MenuList listItems={deviceInfoSetupMenu} disableGutters />
            <Button variant="text" color="error" size="large" onClick={handleUnpairDevice}>
              <Trans>Unpair ELEMNT</Trans>
            </Button>
          </Box>
        </Box>
      </Container>
    </>
  );
}

export default DeviceConfig;
